<!--
  DesignSizeRow.vue -- one SIZE on the design detail page.

  Card: Decoration color + family model (https://trello.com/c/2hcgDM7J)
  Handoff: planning/design-handoffs/handoff_2026-08-23/
           2026-08-23-decoration-family-model/
  ADR 0067 + adr/0067-AMENDMENT-threadflow.md.
  Locked Decisions 8, 11, 13, 16.

  What the row shows, in order (Locked Decision 8)
  ================================================
  1. dimensions + substrate
  2. placement count   (see "the zone gap" below)
  3. production state + spec
  4. per-client approval

  Nothing else. Everything further -- the previews, the production
  ladder, the per-client actions, the change-request text -- belongs in
  the size flyover (Locked Decisions 17-22), which is a separate unit.
  Adding "just one more" field here is how the row becomes the flyover.

  The zone gap, stated rather than papered over
  ============================================
  Locked Decision 8 asks for "zone + placement count". Zone is not a
  property of a size: it lives on `Placement`
  (api/src/models/placement.py:124), and one size can be placed in
  several zones on several products. The family read model therefore
  exposes `placement_count` and no zone at all. Naming a zone on this
  row would mean either inventing one or silently picking the first of
  N -- so the row states the count, which is real, and the zone
  breakdown stays with "Where it's used", which is keyed by placement
  and can say it truthfully.

  Approval: one pill per client, and never broader
  ================================================
  ADR 0067's load-bearing rule. The pills come from `approvalsForSize`
  -- the single selector the client-first accordion also derives from
  -- so a client's state on this row is by construction the same value
  the accordion shows for this (size, client) cell. This component
  never reads `size.approvals` directly; doing so would re-open exactly
  the drift the selector exists to close.

  ADR 0070 -- the store mount's ONE column
  ========================================
  On the store mount of this page a store has exactly one client, so
  the row carries one approval column: that client's. `clientId` is
  handed to the SAME selector as an options argument -- "same selector,
  filtered", ADR 0070 verbatim. This component grows no branch: the
  template renders whatever the selector returns, one entry or four,
  and does not know which mount it is in. A `mode` prop that switched
  rendering here is the maintenance risk ADR 0070 names in its own
  Consequences, and it would be the first place store-specific
  behaviour accreted.

  No colorway line: per the 0067 amendment, colorway moved from the
  size to the variant, so "which threads?" is a routed answer that
  needs a garment colour to be well-formed. A `Colourway - X` slot on a
  size would be a display slot whose question has no answer at that
  grain.
-->
<template>
    <div class="dsr" :data-testid="`design-size-row-${size.id}`">
        <!-- 1. Dimensions + substrate. -->
        <div class="dsr__cell dsr__identity">
            <div class="dsr__dimline">
                <span
                    class="dsr__dims"
                    :data-testid="`design-size-row-dims-${size.id}`"
                >{{ dimensions }}</span>
                <span
                    v-if="substrateLabel"
                    class="dsr__substrate"
                    :class="`dsr__substrate--${size.substrate_target}`"
                    :data-testid="`design-size-row-substrate-${size.id}`"
                >{{ substrateLabel }}</span>
            </div>

            <!-- 2. Placement count. -->
            <div
                class="dsr__usage"
                :data-testid="`design-size-row-usage-${size.id}`"
            >{{ usageText }}</div>

            <!--
              An approval was given against a version. When that is not
              the design's current version, say so on the row rather
              than letting the page imply the approval is current
              (ADR 0016 governs staleness).
            -->
            <div
                v-if="offCurrentVersion"
                class="dsr__offversion"
                :data-testid="`design-size-row-offversion-${size.id}`"
            >On v{{ sizeVersionNumber }}</div>
        </div>

        <!-- 3. Production state + spec. Independent of approval. -->
        <div class="dsr__cell dsr__production">
            <span
                class="dsr__prodchip"
                :class="production.ready
                    ? 'dsr__prodchip--ready'
                    : 'dsr__prodchip--needs'"
                :data-testid="`design-size-row-production-${size.id}`"
            >{{ productionLabel }}</span>
            <div
                v-if="productionSpec"
                class="dsr__spec"
                :data-testid="`design-size-row-spec-${size.id}`"
            >{{ productionSpec }}</div>
        </div>

        <!-- 4. Per-client approval -- one pill per client, this size. -->
        <div class="dsr__cell dsr__approvals">
            <span
                v-if="!approvals.length"
                class="dsr__noclient"
                :data-testid="`design-size-row-noclient-${size.id}`"
            >No client yet</span>
            <template v-else>
                <ApprovalStatePill
                    v-for="entry in approvals"
                    :key="entry.clientId"
                    :state="entry.state"
                    :client-name="entry.clientName"
                    :data-testid="
                        `design-size-row-approval-${size.id}-${entry.clientId}`
                    "
                />
            </template>
        </div>
    </div>
</template>

<script>
import { approvalsForSize } from "@/utils/decorationFamilySelectors";

import ApprovalStatePill from "./ApprovalStatePill.vue";

// Trim trailing zeros so 4.00 reads "4" and 3.50 reads "3.5".
function trimNumber(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return String(Number(n.toFixed(2)));
}

export default {
    name: "DesignSizeRow",
    components: { ApprovalStatePill },
    props: {
        /** The whole family payload -- the selector needs its clients. */
        family: {
            type: Object,
            required: true,
        },
        /** One entry from `families[].sizes`. */
        size: {
            type: Object,
            required: true,
        },
        /**
         * ADR 0070 -- narrow the approval column to ONE client, on the
         * store mount. Null (the Library mount) means every client.
         */
        clientId: {
            type: [String, Number],
            default: null,
        },
    },
    computed: {
        /**
         * A size is SPOKEN as its dimensions (ADR 0067), never as an
         * opaque decoration id. Falls back to an honest dash rather
         * than a half-dimension when either value is missing.
         */
        dimensions() {
            const w = trimNumber(this.size.width_in);
            const h = trimNumber(this.size.height_in);
            if (w == null || h == null) return "Dimensions unknown";
            return `${w}" x ${h}"`;
        },
        substrateLabel() {
            const target = this.size.substrate_target;
            if (target === "light") return "Light garments";
            if (target === "dark") return "Dark garments";
            // `any` / null / anything else makes no substrate claim.
            return "";
        },
        usageText() {
            const n = Number(this.size.placement_count ?? 0);
            if (!n) return "Not on a product yet";
            return `${n} placement${n === 1 ? "" : "s"}`;
        },
        production() {
            return this.size.production ?? { state: null, ready: false };
        },
        productionLabel() {
            if (this.production.ready) return "Stitch file in";
            if (this.production.state === "needs_digitizing") {
                return "Needs digitizing";
            }
            // An unfamiliar state reads as unresolved, not as ready --
            // principle 5, fail toward the record.
            return "Needs digitizing";
        },
        /**
         * The spec line beside the production chip.
         *
         * Stitch counts live on the VERSION's digitization record
         * (`versions[].provenance`), which is the only durable stitch
         * figure in the payload. It is labelled as the version's
         * figure rather than presented as this size's own count: a
         * size scaled from another file has no measured count of its
         * own, and implying one would be a fabricated number on a
         * money-adjacent surface.
         */
        productionSpec() {
            const version = this.sizeVersion;
            const prov = version?.provenance;
            if (!prov) return "";
            if (prov.digitized_stitch_count != null) {
                return `${Number(
                    prov.digitized_stitch_count
                ).toLocaleString()} stitches on v${version.version_number}`;
            }
            if (prov.estimated_stitch_count != null) {
                // An uploaded stitch file's own count is final (#4305).
                const mark = prov.stitch_count_final ? "" : "est. ";
                return `${mark}${Number(
                    prov.estimated_stitch_count
                ).toLocaleString()} stitches on v${version.version_number}`;
            }
            return "";
        },
        sizeVersion() {
            const versions = this.family?.versions;
            if (!Array.isArray(versions)) return null;
            return (
                versions.find(
                    (v) => Number(v.id) === Number(this.size.artwork_version_id)
                ) ?? null
            );
        },
        sizeVersionNumber() {
            return this.sizeVersion?.version_number ?? null;
        },
        offCurrentVersion() {
            const current = this.family?.current_version_id;
            if (current == null || this.size.artwork_version_id == null) {
                return false;
            }
            return Number(current) !== Number(this.size.artwork_version_id);
        },
        /**
         * THE selector. Both approval grains on this page bottom out
         * here -- see utils/decorationFamilySelectors.
         */
        approvals() {
            return approvalsForSize(this.family, this.size, {
                clientId: this.clientId,
            });
        },
    },
};
</script>

<style scoped>
.dsr {
    display: grid;
    grid-template-columns: minmax(150px, 1.05fr) minmax(180px, 1.2fr)
        minmax(180px, 1.2fr);
    gap: 14px;
    align-items: start;
    padding: 13px 16px;
    border-top: 1px solid var(--tf-border);
}
.dsr__cell {
    min-width: 0;
}
.dsr__dimline {
    display: flex;
    align-items: baseline;
    gap: 7px;
    flex-wrap: wrap;
}
.dsr__dims {
    font-variant-numeric: tabular-nums;
    font-size: 14px;
    font-weight: 500;
    color: var(--tf-fg-1);
    white-space: nowrap;
}
.dsr__substrate {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: var(--tf-radius-pill);
    font-size: 11px;
    font-weight: 500;
}
.dsr__substrate--light {
    background: var(--tf-surface-muted);
    color: var(--tf-fg-3);
}
.dsr__substrate--dark {
    background: var(--tf-surface-dark);
    color: var(--tf-fg-on-primary);
}
.dsr__usage,
.dsr__spec {
    font-size: 12px;
    color: var(--tf-fg-4);
    margin-top: 3px;
}
.dsr__offversion {
    font-size: 11px;
    color: var(--tf-warning-dark);
    margin-top: 4px;
}
.dsr__prodchip {
    display: inline-flex;
    align-items: center;
    padding: 2px 9px;
    border-radius: var(--tf-radius-pill);
    font-size: 11.5px;
    font-weight: 500;
}
.dsr__prodchip--ready {
    background: var(--tf-chip-success-bg);
    color: var(--tf-success-dark);
}
.dsr__prodchip--needs {
    background: var(--tf-chip-info-bg);
    color: var(--tf-chip-info-fg);
}
.dsr__approvals {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    align-items: center;
}
.dsr__noclient {
    font-size: 12px;
    color: var(--tf-fg-4);
    font-style: italic;
}

@media (max-width: 720px) {
    .dsr {
        grid-template-columns: 1fr;
        gap: 8px;
    }
}
</style>
